// both custom table and provider table ui is same diff in api only
import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import { Box } from 'rebass/styled-components';
import { useForm } from 'react-hook-form';

import { SlidePanel, FormInput, Alert } from 'ui/patterns';
import { Button, Stack, Cluster, Tag } from 'ui/components';

import { useSaveCustomTable, useGetCustomRecords } from 'hooks';

import * as Icon from 'assets/icons';

import { confirmAlert } from 'utils/confirm-alert';
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from 'utils/showToast';

import { CustomTableProps } from './addEditCustomTableTypes';
import { ErrorModal } from 'types/modalTypes';
import { CustomRecord } from 'types/customTables';

export default function AddCustomTable({
  showPanel,
  togglePanel,
  type,
  accountId,
  tableItem,
}: CustomTableProps): ReactElement {
  const [columns, setColumns] = useState<Array<string>>([]);
  const [columnName, setColumnName] = useState<string>();

  const { mutate: saveCustomTable, isLoading } = useSaveCustomTable();
  const { register, handleSubmit, reset } = useForm();

  const tableColumns =
    (tableItem && tableItem.Columns && tableItem?.Columns[0]) || '';

  const sortItem = { sortKey: tableColumns, sortOrder: 'asc' };

  const columnsSearch = {};
  const keywordSearch = '';
  const tableId = tableItem?.Id || 0;
  const currentSortItem = sortItem;
  const selectedTableColumns = tableItem?.Columns || [];

  const { data } = useGetCustomRecords(
    columnsSearch,
    currentSortItem,
    keywordSearch,
    accountId,
    tableId,
    selectedTableColumns,
  );

  useEffect(() => {
    if (tableItem) {
      setColumns(tableItem.Columns);
      reset({ 'Table Name': tableItem.TableName });
    }
  }, [tableItem, reset]);

  const tableData = useMemo(() => {
    if (data) {
      return data?.pages[0]?.Data?.map((dataItem: CustomRecord) => {
        if (dataItem) return dataItem;
      }).flat();
    }
  }, [data]);

  function addColumn() {
    if (columnName) {
      const index = columns.findIndex(item => item === columnName);
      if (index === -1) {
        setColumns([...columns, columnName]);
        setColumnName('');
      } else {
        showInfoToast({
          message: `The column name '${columnName}' already exists `,
        });
      }
    }
  }

  function deleteColumn(columnName: string) {
    const index = columns.findIndex(item => item === columnName);
    if (index !== -1) {
      setColumns([...columns.slice(0, index), ...columns.slice(index + 1)]);
    }
  }

  function removeColumn(columnName: string) {
    if (tableItem && tableData && tableData.length > 0) {
      confirmAlert({
        message:
          'The data in this column will be lost. Do you want to proceed?',
        buttons: [
          {
            label: 'Yes',
            onClick: () => {
              deleteColumn(columnName);
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'No',
            onClick: () => {
              //
            },
          },
        ],
      });
    } else deleteColumn(columnName);
  }

  function resetForm(): void {
    reset({
      'Table Name': '',
    });
    setColumns([]);
    setColumnName('');
  }

  const handleSave = (data: { 'Table Name': string }) => {
    saveCustomTable(
      {
        AccountId: accountId,
        Id: tableItem?.Id || 0,
        Columns: columns,
        TableName:
          type === 'Provider Table' ? 'ProviderList' : data['Table Name'],
      },
      {
        onSuccess: () => {
          showSuccessToast({
            message: 'Your changes were successfully saved.',
          });
          resetForm();
          togglePanel();
        },
        onError: (error: ErrorModal) => {
          showErrorToast({
            message: error.Errors[0].Message,
          });
        },
      },
    );
  };

  function onsubmit(data: { 'Table Name': string }) {
    const columnsArray = columns.filter(item => item !== '');
    if (columnsArray.length === 0) {
      showErrorToast({
        message: 'There must be at least one column',
      });
    } else handleSave(data);
  }

  const addActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          resetForm();
          togglePanel();
        }}
      >
        Cancel
      </Button>
      <Button
        disabled={isLoading}
        onClick={handleSubmit(onsubmit)}
        variant="primary"
      >
        Save
      </Button>
    </>
  );

  const colsTorender = columns.filter(item => !!item);

  const handleColumnNameChange = (text: string) => {
    setColumnName(text.replace(/\./g, ''));
  };

  return (
    <>
      <SlidePanel
        title={`${tableItem === undefined ? 'Create New Table' : 'Edit Table'}`}
        actions={addActions()}
        size="sm"
        onClose={() => {
          resetForm();
          togglePanel();
        }}
        open={showPanel}
      >
        <Stack direction="y" gap={4}>
          <Alert variant="danger" icon={<Icon.ExclamationCircle />}>
            Existing records will be lost, if columns with values are deleted.
          </Alert>
          <Alert variant="warning" icon={<Icon.Warning />}>
            Adding more than one column with the same name will cause an error
            and the table will not save.
          </Alert>

          {type === 'Provider Table' ? (
            <FormInput
              label="Table Name"
              required
              disabled
              value={'ProviderList'}
            />
          ) : (
            <FormInput label="Table Name" required register={register} />
          )}

          <Box
            sx={{
              display: 'grid',
              gridGap: 3,
              gridTemplateColumns: '1fr minmax(50px, 15%)',
              alignItems: 'end',
            }}
          >
            <FormInput
              label="Column Name"
              type="text"
              onTextChange={handleColumnNameChange}
              value={columnName}
            />
            <Button
              variant="primary"
              style={{ height: '35px' }}
              iconBefore={<Icon.Add />}
              disabled={columnName ? false : true}
              onClick={addColumn}
            />
          </Box>
          <Cluster gap={3}>
            <Box>
              {colsTorender.map((item, index) => (
                <Tag
                  onClose={() => {
                    removeColumn(item);
                  }}
                  key={`${item}${index}`}
                >
                  {item}
                </Tag>
              ))}
            </Box>
          </Cluster>
        </Stack>
      </SlidePanel>
    </>
  );
}
